import type { DailyEventObjectNetworkConnectionEvent } from '@daily-co/daily-js';
import {
  RTVIMessage,
  RTVIMessageType,
  type PipecatClientOptions,
  type Tracks,
} from '@pipecat-ai/client-js';
import { DailyTransport as PipecatDailyTransport } from '@pipecat-ai/daily-transport';
import { RtviHandshakeTimeoutError } from './rtvi-handshake-error';
import { FLOE_AVATAR_USER_NAME } from './types';
import { voiceStartupTimeline } from './voice-startup-timing';

/**
 * Ceiling on the wait for the Pipecat bot to appear in the room.
 *
 * The wait had NO timeout: a bot that never joined — a spawn that died, a
 * Pipecat Cloud cold start that gave up, a room the bot was refused from — left
 * `sendReadyMessage()` pending forever, so the session sat on "Connecting…"
 * with nothing to report and no error to catch. Generous on purpose: a warm
 * spawn puts the bot in the room ~5s after the click and even a cold one is
 * well inside this, so the only thing that trips it is a bot that is not
 * coming. Failing at 45s is still the fastest honest answer available.
 */
export const PROTOCOL_BOT_JOIN_TIMEOUT_MS = 45_000;

/**
 * Ceiling on the wait for Daily to say an app message can be sent (FLO-1140).
 *
 * Daily's `join()` resolves at the SIGNALLING join, and the bot is already in
 * the participant list then. App messages do not go out on signalling presence
 * though: in SFU topology the call machine sends them through its SFU module,
 * which throws until its own room join has started (~0.5s later on a slow
 * device) — and the call machine swallows that throw in an empty `catch`. So a
 * `client-ready` sent the instant the join resolved, to a bot that was already
 * there, was dropped without an error: no `bot-ready`, and every queued
 * client→bot message waited on it forever.
 *
 * `network-connection` is how Daily reports that the path is up. It reports
 * late rather than early — after the local tracks are published — so a slow
 * ICE with the microphone on can reach this ceiling with the path long usable.
 * Past it `client-ready` goes out anyway: right for that case, no worse than
 * before for a Daily that never reports at all, and the `bot-ready` deadline
 * below decides whether it worked.
 */
export const APP_MESSAGE_CHANNEL_TIMEOUT_MS = 10_000;

/**
 * Ceiling on the wait for the bot's `bot-ready` once `client-ready` is sent.
 *
 * The bot answers in ~0.3s. Until it does the SDK sends it nothing — typed
 * text, `site_init`, language and audio settings all queue behind `bot-ready`
 * — so a session still waiting after this long is not one the visitor can use,
 * however alive the greeting makes it look.
 */
export const RTVI_BOT_READY_TIMEOUT_MS = 20_000;

type NamedParticipant = {
  local: boolean;
  name?: string;
  user_name?: string;
};

function isAvatarParticipant(participant: NamedParticipant | null | undefined): boolean {
  const name = participant?.user_name ?? participant?.name ?? '';
  return name.toLowerCase() === FLOE_AVATAR_USER_NAME;
}

function isProtocolBotParticipant(
  participant: NamedParticipant | null | undefined,
): participant is NamedParticipant {
  return !!participant && !participant.local && !isAvatarParticipant(participant);
}

function isDemoScreenParticipant(participant: NamedParticipant | undefined): boolean {
  const name = (participant?.user_name ?? participant?.name ?? '').toLowerCase();
  return !!participant && !participant.local && !isAvatarParticipant(participant)
    && (name.includes('demo agent') || name.includes('onboarding assistant'));
}

/**
 * Daily rooms can contain both the Pipecat bot and a separately published
 * avatar. The upstream transport assumes every room has exactly one remote
 * participant, so the avatar can otherwise consume the one-shot RTVI ready
 * handshake and bot lifecycle callbacks.
 */
export class FloeDailyTransport extends PipecatDailyTransport {
  /**
   * Handshake facts, kept from `initialize()` on rather than sampled when the
   * handshake starts: a browser that joins BEFORE the bot hears `connected`
   * seconds before it needs the answer.
   */
  private appMessageChannelOpen = false;
  private botAnswered = false;
  /** Handshake steps in flight; each re-reads its condition when a fact moves. */
  private readonly handshakeWaits = new Set<{ cancel: () => void; recheck: () => void }>();
  private leaving: Promise<void> | undefined;

  override initialize(
    options: PipecatClientOptions,
    messageHandler: (event: RTVIMessage) => void,
  ): void {
    // The handshake hears each fact BEFORE anything the upstream transport
    // attaches: these listeners are registered ahead of super.initialize(), and
    // bot-ready is read off the message on its way to PipecatClient (below).
    // Daily abandons an event's remaining listeners when one throws, and the
    // upstream ones run the SDK's callbacks and the host page's own handlers
    // (`botReady` is a public event). A handshake that listened behind them
    // could be starved of the very event it waits for by a throw in any of
    // those, and would then tear down a session that had in fact connected.
    const daily = this.dailyCallClient;
    const forgetLastMeeting = () => {
      this.appMessageChannelOpen = false;
      this.botAnswered = false;
    };
    daily.on('joining-meeting', forgetLastMeeting);
    daily.on('left-meeting', () => {
      forgetLastMeeting();
      for (const wait of this.handshakeWaits) {
        wait.cancel();
      }
    });
    daily.on('participant-joined', () => this.recheckHandshake());
    daily.on('network-connection', (event) => {
      this.appMessageChannelOpen = this.appMessageChannelAfter(event);
      this.recheckHandshake();
    });
    const callbacks = options.callbacks;
    super.initialize(
      {
        ...options,
        callbacks: {
          ...(callbacks ?? {}),
          onBotConnected: (participant) => {
            if (!isAvatarParticipant(participant)) {
              callbacks?.onBotConnected?.(participant);
            }
          },
          onBotDisconnected: (participant) => {
            if (!isAvatarParticipant(participant)) {
              callbacks?.onBotDisconnected?.(participant);
            }
          },
          // Daily distinguishes screen tracks from cameras. The SDK's existing
          // demo surface consumes bot-video callbacks; preserve that contract
          // for the positively identified bot without borrowing avatar/visitor
          // screen shares. Keep native screen callbacks available to consumers.
          onScreenTrackStarted: (track, participant) => {
            if (track.kind === 'video' && isDemoScreenParticipant(participant)) {
              callbacks?.onTrackStarted?.(track, participant);
            }
            callbacks?.onScreenTrackStarted?.(track, participant);
          },
          onScreenTrackStopped: (track, participant) => {
            if (track.kind === 'video' && isDemoScreenParticipant(participant)) {
              callbacks?.onTrackStopped?.(track, participant);
            }
            callbacks?.onScreenTrackStopped?.(track, participant);
          },
        },
      },
      (message) => {
        if (message.type === RTVIMessageType.BOT_READY) {
          this.botAnswered = true;
          this.recheckHandshake();
        }
        messageHandler(message);
      },
    );
  }

  override async sendReadyMessage(): Promise<void> {
    // Reached once the local join has resolved — the segment the bot log cannot
    // see (its `client_connected` fires when the SFU accepts the join, up to
    // ~2s before the browser's own join promise settles).
    voiceStartupTimeline.mark('dailyJoined');
    // Avatar-enabled Pipecat bots intentionally publish no audio, so the
    // protocol participant's presence stands in for the remote audio track the
    // upstream transport waits for; waiting for that track can deadlock when
    // the avatar is unavailable.
    //
    // A bot that never joins must fail loudly. Without the deadline the promise
    // is unsettleable and the visitor watches "Connecting…" until they leave —
    // the caller already has a full teardown-and-surface path for a rejected
    // connect, and it was simply never reachable from here.
    await this.waitUntil(
      () => this.hasProtocolBotParticipant(),
      PROTOCOL_BOT_JOIN_TIMEOUT_MS,
      () =>
        new Error(
          `The Floe bot did not join the room within ${PROTOCOL_BOT_JOIN_TIMEOUT_MS}ms`,
        ),
    );
    voiceStartupTimeline.mark('botParticipantSeen');

    // Presence is not a send path (see APP_MESSAGE_CHANNEL_TIMEOUT_MS): a bot
    // that was in the room first is "present" before Daily can carry a message
    // to it. No error at this ceiling — the step gives up waiting and sends.
    await this.waitUntil(() => this.appMessageChannelOpen, APP_MESSAGE_CHANNEL_TIMEOUT_MS);

    this.botAnswered = false;
    this.setReadyState();
    this.flushBufferedAudio();
    this.sendMessage(RTVIMessage.clientReady());
    voiceStartupTimeline.mark('clientReadySent');
    this.stopBufferedAudioRecording();

    // Hold the connect open until the bot answers. `client-ready` is sent ONCE:
    // the bot's handler and the SDK's `onBotReady` both run per message, so a
    // resend that crossed a slow reply would start the session twice. A bot
    // that never answers fails the connect instead, which the caller tears
    // down and reports.
    await this.waitUntil(
      () => this.botAnswered,
      RTVI_BOT_READY_TIMEOUT_MS,
      () => new RtviHandshakeTimeoutError(RTVI_BOT_READY_TIMEOUT_MS),
    );
  }

  /**
   * One leave per teardown. PipecatClient answers a rejected handshake by
   * disconnecting, and a teardown is what rejects the handshake — so a session
   * ended mid-handshake reached Daily's `leave()` twice. Daily keeps a single
   * resolver for it: the second call orphaned the first, and the SDK's own
   * awaited disconnect never returned.
   */
  override _disconnect(): Promise<void> {
    this.leaving ??= this.leaveOnce();
    return this.leaving;
  }

  private async leaveOnce(): Promise<void> {
    try {
      await super._disconnect();
    } finally {
      this.leaving = undefined;
    }
  }

  override tracks(): Tracks {
    const tracks = super.tracks();
    const bot = Object.values(this.dailyCallClient.participants() ?? {}).find(
      isProtocolBotParticipant,
    );

    if (!bot) {
      delete tracks.bot;
      return tracks;
    }

    tracks.bot = {
      audio: bot.tracks?.audio?.persistentTrack ?? undefined,
      video: (isDemoScreenParticipant(bot) ? bot.tracks?.screenVideo?.persistentTrack : undefined)
        ?? bot.tracks?.video?.persistentTrack ?? undefined,
    };
    return tracks;
  }

  private hasProtocolBotParticipant(): boolean {
    return Object.values(this.dailyCallClient.participants() ?? {}).some(
      isProtocolBotParticipant,
    );
  }

  private setReadyState(): void {
    if (this._state === 'ready') return;
    this._state = 'ready';
    this._callbacks.onTransportStateChanged?.('ready');
  }

  private flushBufferedAudio(): void {
    const transport = this as unknown as { flushAudioQueue: () => void };
    transport.flushAudioQueue();
  }

  private stopBufferedAudioRecording(): void {
    const transport = this as unknown as { stopRecording: () => void };
    transport.stopRecording();
  }

  /**
   * Whether an app message sent after this event reaches the wire.
   *
   * SFU: open once the call machine's SFU module is in its room, closed while
   * its socket is down. Peer-to-peer: open once the data channel to the bot
   * (not to the avatar) is. Everything else — signalling's own events
   * included, which the SFU repeats for itself — leaves it as it was.
   */
  private appMessageChannelAfter(event: DailyEventObjectNetworkConnectionEvent): boolean {
    const connected = event.event === 'connected';
    if (event.type === 'sfu' && (connected || event.event === 'interrupted')) {
      return connected;
    }
    const bot =
      event.type === 'peer-to-peer' && connected && event.session_id
        ? this.dailyCallClient.participants()?.[event.session_id]
        : undefined;
    return isProtocolBotParticipant(bot) || this.appMessageChannelOpen;
  }

  private recheckHandshake(): void {
    for (const wait of this.handshakeWaits) {
      wait.recheck();
    }
  }

  /**
   * One step of the handshake: resolves once `isMet()` holds. Every step ends
   * the same other ways — the meeting ends or the connect is aborted (rejects),
   * or `timeoutMs` passes (rejects with `timeoutError`, or resolves when the
   * step has none and carries on without its condition).
   */
  private waitUntil(
    isMet: () => boolean,
    timeoutMs: number,
    timeoutError?: () => Error,
  ): Promise<void> {
    const signal = this._abortController?.signal;
    return new Promise((resolve, reject) => {
      const settle = (error?: Error) => {
        clearTimeout(timer);
        this.handshakeWaits.delete(wait);
        signal?.removeEventListener('abort', wait.cancel);
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      };
      const wait = {
        cancel: () => settle(new Error('RTVI readiness was cancelled before the bot was ready')),
        recheck: () => {
          if (isMet()) {
            settle();
          }
        },
      };
      const timer = setTimeout(() => settle(timeoutError?.()), timeoutMs);
      this.handshakeWaits.add(wait);
      signal?.addEventListener('abort', wait.cancel, { once: true });

      if (signal?.aborted) {
        wait.cancel();
      } else {
        wait.recheck();
      }
    });
  }
}
